import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@libs/zapi-ui/ds/components/button";
import { Spinner } from "@libs/zapi-ui/ds/components/spinner";
import { ConfirmDialog } from "@/zapi-frontend/shared/ui/ConfirmDialog/ConfirmDialog";
import { UnsavedChangesPrompt } from "@/zapi-frontend/shared/ui/UnsavedChangesDialog/UnsavedChangesPrompt";
import { useProjectStore } from "@/zapi-frontend/stores/project";
import { useProjectSetupStore } from "@/zapi-frontend/stores/projectSetup";
import { useCaseFrameLeaveGuard } from "@/zapi-frontend/stores/caseFrameLeaveGuard";
import { useCaseFrameChatDoc } from "@/zapi-frontend/stores/caseFrameChatDoc";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";
import { useProjectGenerationState } from "@/zapi-frontend/shared/hooks/useProjectGenerationState";
import { useChatStore } from "@/zapi-frontend/stores/chatStore";
import { hasEditAccess } from "@/zapi-frontend/shared/utils/permissions";
import { getRouteProjectDetails } from "@/zapi-frontend/shared/const/router";
import { GET_PROJECTS_LIST_KEY } from "@/zapi-frontend/shared/services/useGetProjectsList";
import $api from "@/zapi-frontend/shared/api/axios";
import { CaseBriefEditor } from "@/zapi-frontend/widgets/CreateProjectChat/ui/CaseBriefEditor";
import { FrameDocument } from "./FrameDocument";
import { CaseFrameInstructions } from "./CaseFrameInstructions";
import { ArrowCounterClockwiseIcon } from "@phosphor-icons/react";

// Normalization also covers serializer differences (escapes, smart quotes) so a
// tiptap round-trip of unchanged text does not read as a content change.
function caseBriefContentSignature(md: string): string {
  return md
    .replace(/<[^>]*>/g, "")
    .replace(/^#{1,6}\s+/gm, "")
    .replace(/^\s*>\s?/gm, "")
    .replace(/^\s*([-*+]|\d+\.)\s+/gm, "")
    .replace(/[*_~`\\]/g, "")
    .replace(/[“”«»]/g, '"')
    .replace(/[‘’]/g, "'")
    .replace(/[–—]/g, "-")
    .replace(/\s+/g, " ")
    .trim();
}

const triggerAutoSetup = async (projectId: string, model?: string, prefill = true): Promise<number | null> => {
  const response = await $api.post<{ taskId: number }>(`/projects/${projectId}/auto-setup`, { model, prefill });
  return response.data.taskId;
};

export function CaseFrameIsland() {
  const { t } = useTranslation(["projectHome", "common"]);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { selectedProject, updateProject } = useProjectStore();
  const { setSetupTask } = useProjectSetupStore();
  const { role, accessMode } = useMeStore();
  const { modelName: selectedModel } = useChatStore();
  const hasEdit = hasEditAccess(role, accessMode);
  // The brief drives the running analysis, so it is read-only until it finishes.
  const { isSetupActive } = useProjectGenerationState(selectedProject ? String(selectedProject.id) : "");

  const { pendingLeave, setDirty, setPendingLeave } = useCaseFrameLeaveGuard();

  const [content, setContent] = useState(() => selectedProject?.caseBriefMdContent || "");
  const [isRelaunchOpen, setIsRelaunchOpen] = useState(false);
  const [isLaunching, setIsLaunching] = useState(false);
  const [isSavingLeave, setIsSavingLeave] = useState(false);

  const [initialSignature] = useState(() => caseBriefContentSignature(selectedProject?.caseBriefMdContent || ""));
  const savedContentRef = useRef(selectedProject?.caseBriefMdContent || "");
  const savedSignatureRef = useRef(initialSignature);
  // Relaunch baseline for legacy projects with a null caseBriefMdAtLaunch: the content as loaded.
  const fallbackLaunchSignatureRef = useRef(initialSignature);
  const autosaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const autosaveAbortRef = useRef<AbortController | null>(null);

  const projectId = selectedProject?.id;
  const initializedProjectIdRef = useRef(projectId);

  useEffect(() => {
    const initial = selectedProject?.caseBriefMdContent || "";
    setContent(initial);
    savedContentRef.current = initial;
    savedSignatureRef.current = caseBriefContentSignature(initial);
    fallbackLaunchSignatureRef.current = savedSignatureRef.current;
    initializedProjectIdRef.current = projectId;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  useEffect(() => {
    // Chat edits are persisted server-side by the agent, so they count as saved.
    useCaseFrameChatDoc.getState().setApplyChatUpdate((next) => {
      savedContentRef.current = next;
      savedSignatureRef.current = caseBriefContentSignature(next);
      setContent(next);
    });
    return () => useCaseFrameChatDoc.getState().setApplyChatUpdate(null);
  }, []);

  // Derived from the store on every render so a background refetch (e.g. after a new
  // version consumed the pending edits) updates the button without a remount.
  const atLaunch = selectedProject?.caseBriefMdAtLaunch;
  const launchSignature = useMemo(() => (atLaunch != null ? caseBriefContentSignature(atLaunch) : null), [atLaunch]);

  // A missing analysisLaunchedAt means no analysis has run since the instructions were
  // edited: the field is written on every launch, and only edits made after this feature
  // shipped set analysisInstructionsUpdatedAt in the first place.
  const instructionsUpdatedAt = selectedProject?.analysisInstructionsUpdatedAt;
  const analysisLaunchedAt = selectedProject?.analysisLaunchedAt;
  const instructionsChanged = instructionsUpdatedAt
    ? !analysisLaunchedAt || new Date(instructionsUpdatedAt) > new Date(analysisLaunchedAt)
    : false;

  const contentSignature = caseBriefContentSignature(content);
  const isProjectSynced = initializedProjectIdRef.current === projectId;
  const briefChanged = contentSignature !== (launchSignature ?? fallbackLaunchSignatureRef.current);
  const needsRelaunch = isProjectSynced && (briefChanged || instructionsChanged);
  const hasUnsavedChanges = isProjectSynced && contentSignature !== savedSignatureRef.current;

  useEffect(() => {
    setDirty(hasEdit && hasUnsavedChanges);
  }, [hasEdit, hasUnsavedChanges, setDirty]);

  useEffect(
    () => () => {
      setDirty(false);
      setPendingLeave(null);
    },
    [setDirty, setPendingLeave]
  );

  const autosaveFormattingOnly = useCallback(
    async (next: string) => {
      if (!projectId) return;
      const isFormattingOnly = caseBriefContentSignature(next) === savedSignatureRef.current;
      if (next === savedContentRef.current || !isFormattingOnly) return;

      autosaveAbortRef.current?.abort();
      const controller = new AbortController();
      autosaveAbortRef.current = controller;
      try {
        await $api.patch(`/projects/${projectId}/details`, { caseBriefMdContent: next }, { signal: controller.signal });
        if (controller.signal.aborted) return;
        savedContentRef.current = next;
      } catch (error) {
        if (controller.signal.aborted) return;
        console.error("Failed to autosave case frame:", error);
      }
    },
    [projectId]
  );

  useEffect(() => {
    if (!projectId) return;
    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    autosaveTimerRef.current = setTimeout(() => void autosaveFormattingOnly(content), 500);
    return () => {
      if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    };
  }, [content, projectId, autosaveFormattingOnly]);

  const handleSaveAndRelaunch = useCallback(async () => {
    if (!projectId) return;
    setIsRelaunchOpen(false);
    setIsLaunching(true);
    try {
      await updateProject(String(projectId), { caseBriefMdContent: content });
      await queryClient.invalidateQueries({ queryKey: [GET_PROJECTS_LIST_KEY], refetchType: "all" });

      const response = await $api.post<{ data: { id: number } }>(`/projects/${projectId}/duplicate-inputs`, {});
      const targetId = String(response.data.data.id);

      const taskId = await triggerAutoSetup(targetId, selectedModel, true);
      if (taskId) setSetupTask(targetId, taskId);

      navigate(getRouteProjectDetails(targetId));
    } catch (error) {
      console.error("Failed to relaunch analysis:", error);
      toast.error(t("caseFrame.relaunchFailed"));
      setIsLaunching(false);
    }
  }, [projectId, content, updateProject, queryClient, selectedModel, setSetupTask, navigate, t]);

  const handleLeaveWithoutSaving = useCallback(() => {
    const leave = pendingLeave;
    setPendingLeave(null);
    setDirty(false);
    leave?.();
  }, [pendingLeave, setPendingLeave, setDirty]);

  const handleSaveAndLeave = useCallback(async () => {
    if (!projectId || !pendingLeave) return;
    setIsSavingLeave(true);
    try {
      if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
      autosaveAbortRef.current?.abort();
      await updateProject(String(projectId), { caseBriefMdContent: content });
      savedContentRef.current = content;
      savedSignatureRef.current = caseBriefContentSignature(content);
      await queryClient.invalidateQueries({ queryKey: [GET_PROJECTS_LIST_KEY], refetchType: "all" });
      const leave = pendingLeave;
      setPendingLeave(null);
      setDirty(false);
      leave();
    } catch (error) {
      console.error("Failed to save changes:", error);
      toast.error(t("caseFrame.saveFailed"));
      setIsSavingLeave(false);
    }
  }, [projectId, pendingLeave, content, updateProject, queryClient, setPendingLeave, setDirty, t]);

  if (!selectedProject) return null;

  const showEditor = Boolean(content) || hasEdit;

  return (
    <>
      <FrameDocument
        crumb={t("sidebar.caseFrame")}
        fill={showEditor}
        actions={
          hasEdit ? (
            <div className="flex items-center gap-2">
              <CaseFrameInstructions />
              <Button
                type="button"
                data-testid="case-frame-relaunch-button"
                onClick={() => setIsRelaunchOpen(true)}
                disabled={!needsRelaunch || isLaunching}
              >
                {isLaunching ? (
                  <>
                    <Spinner data-icon="inline-start" aria-label={t("common:loading")} />
                    {t("caseFrame.relaunching")}
                  </>
                ) : (
                  <>
                    <ArrowCounterClockwiseIcon />
                    {t("caseFrame.relaunch")}
                  </>
                )}
              </Button>
            </div>
          ) : undefined
        }
      >
        {showEditor ? (
          <CaseBriefEditor
            appearance="document"
            content={content}
            onUpdate={setContent}
            readOnly={!hasEdit || isLaunching || isSetupActive}
            caseBriefId={selectedProject.caseBriefId}
            projectId={Number(selectedProject.id)}
          />
        ) : (
          <p className="typo-body-sm text-muted-foreground">{t("caseFrame.empty")}</p>
        )}

        <ConfirmDialog
          isOpen={isRelaunchOpen}
          onClose={() => setIsRelaunchOpen(false)}
          onConfirm={handleSaveAndRelaunch}
          title={t("caseFrame.relaunch")}
          description={t("caseFrame.relaunchDescription")}
          confirmText={t("caseFrame.relaunchConfirm")}
          cancelText={t("common:cancel")}
          confirmVariant="default"
        />

        <UnsavedChangesPrompt
          open={pendingLeave !== null}
          isSaving={isSavingLeave}
          onClose={() => setPendingLeave(null)}
          onLeaveWithoutSaving={handleLeaveWithoutSaving}
          onSaveAndLeave={() => void handleSaveAndLeave()}
        />
      </FrameDocument>
    </>
  );
}
